import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Clock, BarChart3, ArrowRight, BookOpen } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}).finally(() => setFetching(false));
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0B0C10]">
        <div className="h-10 w-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="grain bg-[#0B0C10] min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-16 px-6 md:px-12 lg:px-24 border-b border-[#1F222E] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/rr8uc9t9_18167.jpg" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/85 to-[#0B0C10]/70" />
        </div>
        <div className="relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Member Library</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white font-light mt-4">Welcome back, {user.name?.split(" ")[0]}.</h1>
          <p className="text-[#9CA3AF] mt-4 max-w-xl">Free, self-paced courses on branding, marketing, and business fundamentals — designed to help you build, grow, and scale.</p>
          {user.role === "admin" && (
            <Link to="/admin" className="inline-flex items-center gap-2 mt-6 text-sm text-[#D4AF37] hover:text-[#F1D570]" data-testid="dashboard-admin-link">
              Go to Admin Dashboard <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </section>

      <section className="relative z-10 py-16 px-6 md:px-12 lg:px-24" data-testid="courses-grid">
        {fetching ? (
          <p className="text-[#9CA3AF]">Loading courses…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c) => (
              <Link key={c.slug} to={`/courses/${c.slug}`} className="group border border-[#1F222E] bg-[#12141A] hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 overflow-hidden" data-testid={`course-card-${c.slug}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={c.cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12141A] to-transparent" />
                  <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-wider text-[#D4AF37] bg-[#0B0C10]/70 backdrop-blur px-3 py-1 rounded-sm">{c.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-white leading-snug">{c.title}</h3>
                  <p className="text-sm text-[#9CA3AF] mt-3 line-clamp-2">{c.summary}</p>
                  <div className="flex items-center gap-5 mt-5 text-xs text-[#6B7280]">
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {c.duration}</span>
                    <span className="flex items-center gap-1.5"><BarChart3 size={14} /> {c.level}</span>
                    <span className="flex items-center gap-1.5"><BookOpen size={14} /> {c.lessons?.length} lessons</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
